//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';

import { IAccountSettingsState } from '@account-settings/redux/accountSettings/accountSettingsModel';
import * as accountSettingsApi from '@apis/account-settings/accountSettingsApi';
import { IGetElevationResponse, ITimeZone } from '@apis/account-settings/accountSettingsApiModel';
import * as gatewayAuthenticationApi from '@apis/auth/gatewayAuthenticationApi';
import AuthType from '@apis/auth/models/AuthType';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import TimeZoneId from '@models/TimeZoneId';
import { createThunk } from '@redux/createThunk';
import { SliceNames } from '@redux/SliceNames';
import { isNotNull } from '@utils/filterPredicates';
import { RequestError } from '@utils/sessionFetch';
import gaApi from 'app/initialization/ga/ga-api';
import { EMPLOYEE_VIEW_OPTIONS } from '@account-settings/accountSettingsConstants';
import { getTrackingActionForEmployeeView } from '@account-settings/redux/accountSettings/accountSettingsUtils';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';

const initialState: IAccountSettingsState = {
  timezones: undefined,
  userAuthType: undefined,
  currentView: undefined,
  isElevated: false,
  elevationExpirationTimestamp: undefined,
  flags: {
    isRequestingTimezones: false,
    isRequestingSetTimezone: false,
    isRequestingUserAuthType: false,
    isRequestingChangePassword: false,
    isRequestingUserView: false,
    isRequestingSetUserView: false,
    isRequestingUserElevation: false,
    isRequestingRevokeUserElevation: false,
    isRequestingElevateUser: false,
  },
  errors: {
    requestTimezonesError: undefined,
    setTimezoneError: undefined,
    requestUserAuthTypeError: undefined,
    changePasswordError: undefined,
    userViewError: undefined,
    setUserViewError: undefined,
    userElevationError: undefined,
    revokeUserElevationError: undefined,
    elevateUserError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsAccount,
  initialState,
  reducers: {
    requestTimezones(state) {
      state.flags.isRequestingTimezones = true;
    },
    receiveTimezonesSucceeded(
      state,
      action: PayloadAction<ITimeZone[]>,
    ) {
      state.timezones = action.payload;
      state.flags.isRequestingTimezones = false;
    },
    receiveSetTimezoneFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.setTimezoneError = action.payload;
      state.flags.isRequestingTimezones = false;
    },
    requestSetTimezone(state) {
      state.flags.isRequestingSetTimezone = true;
    },
    receiveSetTimezoneSucceeded(state) {
      state.flags.isRequestingSetTimezone = false;
    },
    receiveTimezonesFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.requestTimezonesError = action.payload;
      state.flags.isRequestingSetTimezone = false;
    },
    requestUserAuthType(state) {
      state.flags.isRequestingUserAuthType = true;
    },
    receiveUserAuthTypeSucceeded(
      state,
      action: PayloadAction<AuthType | null>,
    ) {
      if (isNotNull(action.payload)) {
        state.userAuthType = action.payload;
      }
      state.flags.isRequestingUserAuthType = false;
    },
    receiveUserAuthTypeFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.requestUserAuthTypeError = action.payload;
      state.flags.isRequestingUserAuthType = false;
    },
    requestChangePassword(state) {
      state.flags.isRequestingChangePassword = true;
      state.errors.changePasswordError = undefined;
    },
    receiveChangePasswordSucceeded(state) {
      state.flags.isRequestingChangePassword = false;
    },
    receiveChangePasswordFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.changePasswordError = action.payload;
      state.flags.isRequestingChangePassword = false;
    },
    requestUserView(state) {
      state.flags.isRequestingUserView = true;
      state.errors.userViewError = undefined;
    },
    receiveUserViewSucceeded(
      state,
      action: PayloadAction<EMPLOYEE_VIEW_OPTIONS | undefined>,
    ) {
      state.flags.isRequestingUserView = false;
      state.currentView = action.payload ?? EMPLOYEE_VIEW_OPTIONS.CLIENT_MODE;
    },
    receiveUserViewFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.userViewError = action.payload;
      state.flags.isRequestingUserView = false;
    },
    requestSetUserView(state) {
      state.flags.isRequestingSetUserView = true;
      state.errors.setUserViewError = undefined;
    },
    receiveSetUserViewSucceeded(
      state,
      action: PayloadAction<EMPLOYEE_VIEW_OPTIONS>,
    ) {
      state.flags.isRequestingSetUserView = false;
      state.currentView = action.payload;
    },
    receiveSetUserViewFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.setUserViewError = action.payload;
      state.flags.isRequestingSetUserView = false;
    },
    requestUserElevation(state) {
      state.flags.isRequestingUserElevation = true;
      state.errors.userElevationError = undefined;
    },
    receiveUserElevationSucceeded(
      state,
      action: PayloadAction<IGetElevationResponse>,
    ) {
      const {
        isElevated,
        expirationTimestamp
      } = action.payload;

      state.flags.isRequestingUserElevation = false;
      state.isElevated = isElevated;
      state.elevationExpirationTimestamp = expirationTimestamp;
    },
    receiveUserElevationFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.userElevationError = action.payload;
      state.flags.isRequestingUserElevation = false;
    },
    requestRevokeUserElevation(state) {
      state.flags.isRequestingRevokeUserElevation = true;
      state.errors.revokeUserElevationError = undefined;
    },
    receiveRevokeUserElevationSucceeded(state) {
      state.flags.isRequestingRevokeUserElevation = false;
      state.isElevated = false;
      state.elevationExpirationTimestamp = undefined;
    },
    receiveRevokeUserElevationFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.revokeUserElevationError = action.payload;
      state.flags.isRequestingRevokeUserElevation = false;
    },
    requestElevateUser(state) {
      state.flags.isRequestingElevateUser = true;
      state.errors.elevateUserError = undefined;
    },
    receiveElevateUserSucceeded(
      state,
      action: PayloadAction<IGetElevationResponse>,
    ) {
      state.flags.isRequestingElevateUser = false;
      state.isElevated = action.payload.isElevated;
      state.elevationExpirationTimestamp = action.payload.expirationTimestamp;
    },
    receiveElevateUserFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.elevateUserError = action.payload;
      state.flags.isRequestingElevateUser = false;
    },
  },
});

export const fetchTimezones = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTimezones());
  try {
    const timezones = await accountSettingsApi.getTimezones();
    dispatch(actions.receiveTimezonesSucceeded(timezones));
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveTimezonesFailed(error.message));
  }
});

export const setTimezone = createThunk((timeZoneId: TimeZoneId) => async (dispatch) => {
  dispatch(actions.requestSetTimezone());
  try {
    await accountSettingsApi.updateTimezone(timeZoneId);
    dispatch(actions.receiveSetTimezoneSucceeded());
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_GENERAL_UPDATE_TIMEZONE,
      gaApi.LABEL_SUCCESS,
    );
    dispatch(toastNotificationsActions.addToastNotification({
      title: 'Time zone successfully updated!',
      type: ToastType.SUCCESS,
    }));
    setTimeout(() => {
      window.location.reload();
      // Wait a brief period so the user can see the success toast.
    }, 2000);
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveSetTimezoneFailed(error.message));
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_GENERAL_UPDATE_TIMEZONE,
      gaApi.LABEL_FAIL,
    );
    dispatch(toastNotificationsActions.addToastNotification({
      title: 'An error occurred when trying to update your time zone.',
      type: ToastType.ERROR,
    }));
  }
});

export const fetchUserAuthType = createThunk((email: string) => async (dispatch) => {
  dispatch(actions.requestUserAuthType());
  try {
    const authType = await gatewayAuthenticationApi.getUserAuthTypeByEmail(email);
    dispatch(actions.receiveUserAuthTypeSucceeded(authType));
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveUserAuthTypeFailed(error.message));
  }
});

export type PASSWORD_CHANGE_STATUS = 'success' | 'password-mismatch' | 'error';
export const changePassword = createThunk((
  currentPassword: string,
  newPassword: string,
  newPasswordConfirmation: string
) => async (dispatch): Promise<PASSWORD_CHANGE_STATUS> => {
  dispatch(actions.requestChangePassword());
  try {
    await accountSettingsApi.changeUserPassword(currentPassword, newPassword, newPasswordConfirmation);
    dispatch(actions.receiveChangePasswordSucceeded());
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_GENERAL_PASSWORD_CHANGE,
      gaApi.LABEL_SUCCESS,
    );
    dispatch(toastNotificationsActions.addToastNotification({
      title: 'Your password has been updated!',
      message: 'Now redirecting you to login.',
      type: ToastType.SUCCESS,
    }));
    setTimeout(() => {
      // Globally sign out the user (across all devices)
      // so that they have to log in with the new password.
      Auth.signOut({ global: true });
      // Wait a brief period so the user can see the success toast.
    }, 2000);
    return 'success';
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveChangePasswordFailed(error.message));
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_GENERAL_PASSWORD_CHANGE,
      gaApi.LABEL_FAIL,
    );
    if (error.message === 'Old password does not match.') {
      return 'password-mismatch';
    } else {
      return 'error';
    }
  }
});

export const fetchUserView = createThunk(() => async (dispatch) => {
  dispatch(actions.requestUserView());
  try {
    const response = await accountSettingsApi.getInternalView();
    dispatch(actions.receiveUserViewSucceeded(response.view));
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveUserViewFailed(error.message));
  }
});

export const setUserView = createThunk((view: EMPLOYEE_VIEW_OPTIONS) => async (dispatch) => {
  dispatch(actions.requestSetUserView());
  try {
    await accountSettingsApi.setInternalView(view);
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      getTrackingActionForEmployeeView(view),
      gaApi.LABEL_SUCCESS,
    );
    dispatch(actions.receiveSetUserViewSucceeded(view));
    window.location.reload();
  } catch (e) {
    const error = e as RequestError;
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      getTrackingActionForEmployeeView(view),
      gaApi.LABEL_FAIL,
    );
    dispatch(toastNotificationsActions.addToastNotification({
      title: 'An error occurred when trying to update feature view.',
      type: ToastType.ERROR,
    }));
    dispatch(actions.receiveSetUserViewFailed(error.message));
  }
});

export const fetchUserElevation = createThunk(() => async (dispatch) => {
  dispatch(actions.requestUserElevation());
  try {
    const response = await accountSettingsApi.getElevation();
    dispatch(actions.receiveUserElevationSucceeded(response));
  } catch (e) {
    const error = e as RequestError;
    dispatch(actions.receiveUserElevationFailed(error.message));
  }
});

export const revokeUserElevation = createThunk(() => async (dispatch) => {
  dispatch(actions.requestRevokeUserElevation());
  try {
    await accountSettingsApi.revokeUserElevation();
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_EMPLOYEE_ELEVATION_DISABLED,
      gaApi.LABEL_SUCCESS,
    );
    dispatch(actions.receiveRevokeUserElevationSucceeded());
    window.location.reload();
  } catch (e) {
    const error = e as RequestError;
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_EMPLOYEE_ELEVATION_DISABLED,
      gaApi.LABEL_FAIL,
    );
    dispatch(toastNotificationsActions.addToastNotification({
      title: 'An error occurred when trying to revoke user elevation.',
      type: ToastType.ERROR,
    }));
    dispatch(actions.receiveRevokeUserElevationFailed(error.message));
  }
});

export const elevateUser = createThunk((reason: string, otherReason: string) => async (dispatch) => {
  dispatch(actions.requestElevateUser());
  try {
    const response = await accountSettingsApi.elevateUser(reason, otherReason);
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_EMPLOYEE_ELEVATION_ENABLED,
      gaApi.LABEL_SUCCESS,
    );
    dispatch(actions.receiveElevateUserSucceeded(response));
    window.location.reload();
  } catch (e) {
    const error = e as RequestError;
    gaApi.recordEvent(
      gaApi.CATEGORY_SETTINGS,
      gaApi.ACTION_EMPLOYEE_ELEVATION_ENABLED,
      gaApi.LABEL_FAIL,
    );
    dispatch(toastNotificationsActions.addToastNotification({
      title: 'An error occurred when trying to elevate user.',
      type: ToastType.ERROR,
    }));
    dispatch(actions.receiveElevateUserFailed(error.message));
  }
});

export default reducer;
